import { usePostHog } from 'posthog-js/react'
import { useEffect } from 'react'

type GroupType = 'organization' | 'location' | 'asset' | 'issue'

type GroupEntity = {
  id: number
  name: string
} | null | undefined

interface UsePosthogGroupOptions {
  clearOnUnmount?: boolean
}

/**
 * Type-safe hook to set PostHog group analytics.
 *
 * @param groupType - The type of group (organization, location, asset, issue)
 * @param entity - The entity to track (must have id and name)
 * @param options - Optional configuration
 * @param options.clearOnUnmount - If true, clears the group when component unmounts
 *
 * @example
 * // Basic usage
 * usePosthogGroup('organization', organization)
 *
 * @example
 * // With cleanup on unmount
 * usePosthogGroup('issue', opportunity, { clearOnUnmount: true })
 */
export function usePosthogGroup(
  groupType: GroupType,
  entity: GroupEntity,
  options: UsePosthogGroupOptions = {}
) {
  const posthog = usePostHog()
  const { clearOnUnmount = false } = options

  useEffect(() => {
    // Conditional check ensures entity is defined before calling posthog.group
    // This prevents issues when the page loads and the entity is temporarily undefined
    if (entity) {
      posthog.group(groupType, entity.id.toString(), { name: entity.name })
    }

    if (clearOnUnmount) {
      return () => {
        posthog.group(groupType, null)
      }
    }
  }, [entity, groupType, clearOnUnmount, posthog])
}